import {
	type ColumnDef,
	DataTable,
	FilterBuilder,
	type FilterField,
	TintedBackgroundIcon,
	useFilterBuilder,
	useUrlState,
} from "@monbits/ui";
import {
	AlertTriangle,
	Ban,
	CheckCircle,
	CircleDashed,
	Clock,
	Flag,
	Pause,
	ShieldAlert,
	ShieldCheck,
	ShieldX,
	XCircle,
} from "lucide-react";
import * as React from "react";
import { Outlet, useNavigate, useSearchParams } from "react-router-dom";
import { CryptoIcon } from "src/components/crypto-icon/crypto-icon";
import { RiskLevelBadge } from "src/components/risk-level-badge";
import { StatusBadge } from "src/components/status-badge";
import { useAMLTransactions } from "src/queries/aml-queries";
import type {
	AMLCheckStatus,
	AMLRiskLevel,
	AMLTransaction,
	AMLTransactionStatus,
} from "src/types";
import {
	getAMLStatusConfig as getUnifiedAMLStatusConfig,
	getRiskLevelConfig as getUnifiedRiskLevelConfig,
	getStatusConfig as getUnifiedStatusConfig,
} from "src/utils/status-config";

// TX status values to show in filter
const TX_STATUSES = [
	"pending",
	"confirmed",
	"on_hold",
	"in_progress",
	"completed",
	"failed",
	"cancelled",
] as const;

// Generate TX status options from unified config
const TX_STATUS_OPTIONS = TX_STATUSES.map((status) => {
	const config = getUnifiedStatusConfig(status);
	return {
		value: status,
		label: config.label,
		icon: (
			<TintedBackgroundIcon icon={config.icon} color={config.color} size="sm" />
		),
	};
});

// AML status values to show in filter
const AML_STATUSES = ["pending", "approved", "flagged", "rejected"] as const;

// Generate AML status options from unified config
const AML_STATUS_OPTIONS = AML_STATUSES.map((status) => {
	const config = getUnifiedAMLStatusConfig(status);
	return {
		value: status,
		label: config.label,
		icon: (
			<TintedBackgroundIcon icon={config.icon} color={config.color} size="sm" />
		),
	};
});

// Risk level values to show in filter
const RISK_LEVELS = ["low", "medium", "high", "critical"] as const;

// Generate risk options from unified config
const RISK_OPTIONS = RISK_LEVELS.map((level) => {
	const config = getUnifiedRiskLevelConfig(level);
	return {
		value: level,
		label: config.label,
		icon: (
			<TintedBackgroundIcon icon={config.icon} color={config.color} size="sm" />
		),
	};
});

const FILTER_FIELDS: FilterField[] = [
	{
		id: "txStatus",
		label: "TX Status",
		type: "select",
		icon: <Clock className="h-4 w-4" />,
		options: TX_STATUS_OPTIONS,
	},
	{
		id: "amlStatus",
		label: "AML Status",
		type: "select",
		icon: <ShieldAlert className="h-4 w-4" />,
		options: AML_STATUS_OPTIONS,
	},
	{
		id: "riskLevel",
		label: "Risk Level",
		type: "select",
		icon: <AlertTriangle className="h-4 w-4" />,
		options: RISK_OPTIONS,
	},
];

const formatStatusLabel = (status: AMLTransactionStatus) => {
	switch (status) {
		case "pending":
			return "Pending";
		case "confirmed":
			return "Confirmed";
		case "on_hold":
		case "onHold":
			return "On Hold";
		case "in_progress":
		case "inProgress":
			return "In Progress";
		case "completed":
			return "Completed";
		case "failed":
			return "Failed";
		case "cancelled":
			return "Cancelled";
		default:
			return status;
	}
};

const formatAMLStatusLabel = (status: AMLCheckStatus) => {
	switch (status) {
		case "pending":
			return "Pending";
		case "approved":
			return "Approved";
		case "flagged":
			return "Flagged";
		case "rejected":
			return "Rejected";
		default:
			return status;
	}
};

const txStatusConfig: Record<
	AMLTransactionStatus,
	{
		type: "success" | "warning" | "error" | "info" | "default";
		icon: typeof CheckCircle;
	}
> = {
	pending: { type: "warning", icon: Clock },
	confirmed: { type: "info", icon: CheckCircle },
	on_hold: { type: "warning", icon: Pause },
	onHold: { type: "warning", icon: Pause },
	in_progress: { type: "info", icon: CircleDashed },
	inProgress: { type: "info", icon: CircleDashed },
	completed: { type: "success", icon: CheckCircle },
	failed: { type: "error", icon: XCircle },
	cancelled: { type: "error", icon: Ban },
};

const amlStatusConfig: Record<
	AMLCheckStatus,
	{
		type: "success" | "warning" | "error" | "info" | "default";
		icon: typeof CheckCircle;
	}
> = {
	pending: { type: "warning", icon: Clock },
	approved: { type: "success", icon: ShieldCheck },
	flagged: { type: "warning", icon: Flag },
	rejected: { type: "error", icon: ShieldX },
};

const getStatusConfig = (status: AMLTransactionStatus) =>
	txStatusConfig[status] ?? { type: "default" as const, icon: Clock };

const getAMLStatusConfig = (status: AMLCheckStatus) =>
	amlStatusConfig[status] ?? { type: "default" as const, icon: Clock };

const columns: ColumnDef<AMLTransaction>[] = [
	{
		id: "id",
		header: "ID",
		cell: (row) => <span className="font-medium">#{row.id}</span>,
	},
	{
		id: "currency",
		header: "Currency",
		cell: (row) => (
			<CryptoIcon
				ticker={row.currency}
				showLabel
				showComposite
				className="h-5 w-5"
			/>
		),
	},
	{
		id: "txStatus",
		header: "TX Status",
		cell: (row) => {
			const config = getStatusConfig(row.status);
			return (
				<StatusBadge type={config.type} icon={config.icon}>
					{formatStatusLabel(row.status)}
				</StatusBadge>
			);
		},
	},
	{
		id: "amlStatus",
		header: "AML Status",
		cell: (row) =>
			row.amlCheck ? (
				(() => {
					const config = getAMLStatusConfig(row.amlCheck.status);
					return (
						<StatusBadge type={config.type} icon={config.icon}>
							{formatAMLStatusLabel(row.amlCheck.status)}
						</StatusBadge>
					);
				})()
			) : (
				<span className="text-muted-foreground text-sm">—</span>
			),
	},
	{
		id: "risk",
		header: "Risk",
		cell: (row) =>
			row.amlCheck ? (
				<RiskLevelBadge riskLevel={row.amlCheck.riskLevel} />
			) : (
				<span className="text-muted-foreground text-sm">—</span>
			),
	},
	{
		id: "score",
		header: "Score",
		cell: (row) =>
			row.amlCheck ? (
				<span className="font-semibold">{row.amlCheck.riskScore}</span>
			) : (
				<span className="text-muted-foreground">—</span>
			),
	},
	{
		id: "amount",
		header: "Amount",
		className: "text-right",
		cell: (row) => (
			<span className="font-mono">{parseFloat(row.amount).toFixed(4)}</span>
		),
	},
];

export function AMLTransactionsList() {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();

	const { filters, setFilters } = useFilterBuilder({
		fields: FILTER_FIELDS,
		searchParams,
		setSearchParams,
	});

	const [pagination, setPagination] = useUrlState({
		page: { type: "number", default: 1 },
		limit: { type: "number", default: 20 },
	});

	const offset = (pagination.page - 1) * pagination.limit;

	// Convert filter builder filters to API params
	const apiFilters = React.useMemo(() => {
		const result: {
			txStatus?: AMLTransactionStatus;
			amlStatus?: AMLCheckStatus;
			riskLevel?: AMLRiskLevel;
		} = {};

		for (const f of filters) {
			if (f.operator === "is") {
				if (f.field === "txStatus") {
					result.txStatus = f.value as AMLTransactionStatus;
				} else if (f.field === "amlStatus") {
					result.amlStatus = f.value as AMLCheckStatus;
				} else if (f.field === "riskLevel") {
					result.riskLevel = f.value as AMLRiskLevel;
				}
			}
		}

		return result;
	}, [filters]);

	const query = useAMLTransactions({
		...apiFilters,
		limit: pagination.limit,
		offset,
	});

	const transactions = query.data?.transactions ?? [];
	const totalCount = query.data?.total ?? 0;
	const isLoading = query.isLoading || query.isFetching;

	const handleRowClick = React.useCallback(
		(tx: AMLTransaction) => {
			navigate(`/aml-transactions/${tx.id}`);
		},
		[navigate],
	);

	return (
		<>
			<div className="p-4 sm:p-6 space-y-6">
				<div className="flex items-center justify-between">
					<div>
						<h1 className="text-3xl font-bold">AML Transactions</h1>
						<p className="text-sm text-muted-foreground mt-1">
							{totalCount} total transaction{totalCount !== 1 ? "s" : ""}
						</p>
					</div>
				</div>

				<FilterBuilder
					fields={FILTER_FIELDS}
					filters={filters}
					onFiltersChange={setFilters}
				/>

				<DataTable
					columns={columns}
					data={transactions}
					isLoading={isLoading}
					onRowClick={handleRowClick}
					getRowId={(row) => String(row.id)}
					emptyMessage={
						filters.length > 0 ? "No transactions found" : "No AML transactions"
					}
					emptyDescription={
						filters.length > 0
							? "No transactions match the selected filters"
							: "There are currently no transactions with AML checks"
					}
					emptyIcon={<AlertTriangle className="h-12 w-12" />}
					pagination={
						totalCount > 0
							? {
									page: pagination.page,
									pageSize: pagination.limit,
									totalCount,
									onPageChange: (page: number) => setPagination({ page }),
									onPageSizeChange: (limit: number) =>
										setPagination({ limit, page: 1 }),
								}
							: undefined
					}
				/>
			</div>

			<Outlet context={{ transactions, isLoading }} />
		</>
	);
}

export type AMLTransactionsOutletContext = {
	transactions: AMLTransaction[];
	isLoading: boolean;
};
